import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { authApi, LoginCredentials } from '@/shared/api/authApi'
import { useSystemStore } from './systemStore'
import { accountInfoApi } from '@/shared/api/accountInfoApi'
import { IProfileResponse } from '@/shared/api/types'
import { abortSessionRequests } from '@/shared/api/sessionAbortController'

interface User {
  id: string
  AuthId: string
  username: string
  email: string
  roles: string[]
}

interface AuthState {
  user: User | null
  token: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  currentServer: string
  userPassword: string | null
  tokenAccount: string | null
  profile: IProfileResponse | null

  login: (credentials: LoginCredentials) => Promise<void>
  setTokenAccount: (account: string) => void
  logout: () => void
  clearError: () => void
  setServer: (server: string) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      tokenAccount: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      currentServer: 'https://hosting.glonasssoft.ru',
      profile: null,
      setTokenAccount: (account) => set({ tokenAccount: account }),
      userPassword: null,

      login: async (credentials: LoginCredentials) => {
        const { currentServer } = get()

        set({ isLoading: true, error: null })

        try {
          const response = await authApi.login(credentials, currentServer)
          const profile = await accountInfoApi.getProfile(
            currentServer,
            response.token
          )

          set({
            user: response.user,
            token: response.token,
            refreshToken: response.refreshToken,
            userPassword: credentials.password,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            profile,
          })
        } catch (error: any) {
          set({
            isLoading: false,
            error: error.message || 'Произошла неизвестная ошибка',
            isAuthenticated: false,
            user: null,
            token: null,
            refreshToken: null,
            userPassword: null,
          })
          throw error
        }
      },
      logout: async (): Promise<void> => {
        const { token, currentServer } = get()

        abortSessionRequests()

        if (token) {
          try {
            await authApi.logout(token, currentServer)
          } catch (error) {
            console.warn('Logout API warning:', error)
          }
        }

        const systemStore = useSystemStore.getState()
        systemStore.clearSelectedSystem()

        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          error: null,
          profile: null,
        })
      },

      clearError: () => {
        set({ error: null })
      },

      setServer: (server: string) => {
        set({ currentServer: server })
        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          error: null,
          profile: null,
        })
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        userPassword: state.userPassword,
        isAuthenticated: state.isAuthenticated,
        currentServer: state.currentServer,
        profile: state.profile,
      }),
    }
  )
)
